import dagre from '@dagrejs/dagre'
import type { Edge, Node } from '@vue-flow/core'

type CompanyOrganization = App.Data.Company.CompanyOrganization
type CompanyHolding = App.Data.Company.CompanyHolding

export type OrgGraphNode = {
  id: string
  name: string
  type: string
  objectCount?: number | null
  missingCount?: number | null
  deletable?: boolean
}

export type OrgGraphEdge = { source: string; target: string; percentage?: number | null }

export type OrgGraph = { nodes: OrgGraphNode[]; edges: OrgGraphEdge[]; rootIds?: string[] }

const NODE_WIDTH = 246
const NODE_HEIGHT = 72
const H_GAP = 32
const LEVEL_GAP = 90

const EDGE_LABEL_PROPS = {
  labelBgPadding: [8, 4] as [number, number],
  labelBgBorderRadius: 999,
  labelBgStyle: { fill: '#ffffff', stroke: '#e2e7e8', strokeWidth: 1 },
  labelStyle: { fill: '#3a4648', fontWeight: 700, fontSize: 11 },
}

export function normalizeOrganization(organization: CompanyOrganization, holdings: CompanyHolding[]): OrgGraph {
  const nodes = new Map<string, OrgGraphNode>()
  const edges = new Map<string, OrgGraphEdge>()

  const addNode = (source: { id: string; name: string; type: string }) => {
    if (!nodes.has(source.id)) {
      nodes.set(source.id, { id: source.id, name: source.name, type: source.type, objectCount: null })
    }
  }

  const addEdge = (source: string, target: string, percentage: number | null) => {
    const key = `${source}->${target}`

    if (!edges.has(key)) {
      edges.set(key, { source, target, percentage })
    }
  }

  const visitShareholders = (org: CompanyOrganization, seen: Set<string>) => {
    addNode(org)

    if (seen.has(org.id)) {
      return
    }

    seen.add(org.id)
    org.shareholders.forEach((shareholder) => {
      addNode(shareholder)
      addEdge(shareholder.id, org.id, shareholder.percentage)
      visitShareholders(shareholder, seen)
    })
  }

  const visitHoldings = (holding: CompanyHolding, parentId: string, seen: Set<string>) => {
    addNode(holding)
    addEdge(parentId, holding.id, holding.percentage)

    if (seen.has(holding.id)) {
      return
    }

    seen.add(holding.id)
    holding.holdings.forEach((child) => visitHoldings(child, holding.id, seen))
  }

  visitShareholders(organization, new Set<string>())
  holdings.forEach((holding) => visitHoldings(holding, organization.id, new Set<string>()))

  return { nodes: [...nodes.values()], edges: [...edges.values()] }
}

function addLevelLabels(nodes: Node[], targetLabel: string): void {
  const rowTops = [...new Set(nodes.map((node) => node.position.y))].sort((a, b) => b - a)
  const minX = nodes.length ? Math.min(...nodes.map((node) => node.position.x)) : 0

  rowTops.forEach((top, level) => {
    nodes.push({
      id: `level-${level}`,
      type: 'level-label',
      position: { x: minX - 130, y: top + NODE_HEIGHT / 2 - 12 },
      data: { text: level === 0 ? targetLabel : `E${level}` },
      draggable: false,
      selectable: false,
    })
  })
}

export function layoutGraph(
  orgGraph: OrgGraph,
  options: { levelLabels: boolean; targetLabel: string },
): { nodes: Node[]; edges: Edge[] } {
  const nodeById = new Map(orgGraph.nodes.map((node) => [node.id, node]))

  const seenEdge = new Set<string>()
  const uniqueEdges = orgGraph.edges.filter((edge) => {
    const key = `${edge.source}->${edge.target}`

    if (seenEdge.has(key) || !nodeById.has(edge.source) || !nodeById.has(edge.target)) {
      return false
    }

    seenEdge.add(key)

    return true
  })

  const graph = new dagre.graphlib.Graph()
  graph.setDefaultEdgeLabel(() => ({}))
  graph.setGraph({ rankdir: 'TB', nodesep: H_GAP, ranksep: LEVEL_GAP })

  nodeById.forEach((_, id) => graph.setNode(id, { width: NODE_WIDTH, height: NODE_HEIGHT }))
  uniqueEdges.forEach((edge) => graph.setEdge(edge.source, edge.target))

  dagre.layout(graph)

  const nodes: Node[] = [...nodeById.values()].map((source) => {
    const { x, y } = graph.node(source.id)

    return {
      id: source.id,
      type: 'organization',
      position: { x: x - NODE_WIDTH / 2, y: y - NODE_HEIGHT / 2 },
      data: source,
      draggable: false,
    }
  })

  if (options.levelLabels) {
    addLevelLabels(nodes, options.targetLabel)
  }

  const edges: Edge[] = uniqueEdges.map((edge) => ({
    id: `${edge.source}->${edge.target}`,
    source: edge.source,
    target: edge.target,
    label: edge.percentage === null || edge.percentage === undefined ? undefined : `${edge.percentage} %`,
    ...EDGE_LABEL_PROPS,
  }))

  return { nodes, edges }
}
